import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allProducts as fallbackProducts } from '@/mocks/productsData';
import { useScrollReveal, useScrollRevealMultiple } from '@/hooks/useScrollReveal';
import { Product, fetchFeaturedProducts } from '@/services/products';
import { withImageFallback } from '@/utils/images';
import { useCart } from '@/contexts/CartContext';
import { HomeSection } from '@/services/homeSections';

type ProductShowcaseProps = {
  section?: HomeSection | null;
};

export default function ProductShowcase({ section }: ProductShowcaseProps) {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>(fallbackProducts);
  const { addToCart, openCart } = useCart();
  const [cartFeedback, setCartFeedback] = useState<{ id: number; message: string } | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { setRef, visible } = useScrollRevealMultiple(products.length);
  const sectionEyebrow = section?.Eyebrow || 'FEATURED PRODUCTS';
  const sectionTitle = section?.Title || '精選助聽器與照護方案';
  const sectionDescription = section?.Description || '由專業團隊依照不同生活需求，整理適合比較與預約試戴的精選商品。';
  const sectionButtonLabel = section?.PrimaryButtonLabel || '查看全部商品';
  const sectionButtonUrl = section?.PrimaryButtonUrl || '/products';
  const sectionNote = section?.NoteText || '可在商品管理中調整精選商品與排序。';
  const sectionTags = section?.Tags?.length ? section.Tags : ['聽力評估', '試戴體驗', '專人諮詢'];
  const sectionBadge = section?.BadgeText || '商品提示';

  useEffect(() => {
    let alive = true;
    fetchFeaturedProducts(6).then((data) => {
      if (alive) setProducts(data);
    });
    return () => {
      alive = false;
    };
  }, []);

  function go(url: string) {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  }

  function productTrialBookingPath(product: Product) {
    return `/booking?${new URLSearchParams({
      type: 'ProductTrial',
      productId: String(product.id),
      productName: product.name,
      productCode: product.productCode || '',
    }).toString()}`;
  }

  function getCardTags(product: Product) {
    return product.tags.map((tag) => tag.trim()).filter(Boolean);
  }

  function handleQuickAddToCart(product: Product) {
    if (product.hasVariants) {
      navigate(`/product/${product.slug || product.id}`);
      return;
    }

    const result = addToCart(product, null, 1);
    if (result.ok) {
      openCart();
    } else {
      setCartFeedback({ id: product.id, message: result.message || '商品加入購物車失敗' });
      window.setTimeout(() => setCartFeedback((current) => (current?.id === product.id ? null : current)), 2500);
    }
  }

  return (
    <section className="relative bg-background-100 py-10 md:py-20 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-accent-500/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-primary-500/6 blur-2xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-secondary-500/5 blur-3xl pointer-events-none" />

      <div className="relative px-6 md:px-10 lg:px-16">
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 scroll-reveal ${headerVisible ? 'scroll-reveal-visible' : ''}`}
        >
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-sparkling-2-line text-accent-600 text-lg" />
                </div>
              </div>
              <span className="text-xs font-bold tracking-[0.2em] text-accent-600 uppercase">
                {sectionEyebrow}
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {sectionTitle}
              <span className="block text-base md:text-lg font-normal text-foreground-600 mt-2">
                {sectionDescription}
              </span>
            </h2>
          </div>
          <button
            onClick={() => go(sectionButtonUrl)}
            className="hidden md:inline-flex items-center gap-2.5 bg-primary-500 text-background-50 px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap group"
          >
            <span className="font-semibold">{sectionButtonLabel}</span>
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-line text-background-50 text-base group-hover:translate-x-0.5 transition-transform duration-300" />
            </div>
          </button>
        </div>

        <div className="mb-10 md:mb-14">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent-50 via-accent-100/60 to-accent-50 border border-accent-200/50">
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute top-0 left-10 w-40 h-full border-l border-dashed border-accent-400" />
              <div className="absolute top-0 left-20 w-40 h-full border-l border-dashed border-accent-400" />
            </div>
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-accent-400/10 blur-2xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-primary-400/8 blur-xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-center gap-5 md:gap-8 px-6 py-6 md:px-10 md:py-7">
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-accent-500 flex items-center justify-center shadow-sm">
                    <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center">
                      <i className="ri-voiceprint-line text-background-50 text-2xl md:text-4xl" />
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-2xl bg-accent-400 blur-md opacity-40 -z-10" />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 mb-2">
                  <div className="w-6 h-px bg-accent-300 hidden md:block" />
                  <span className="text-xs font-bold tracking-[0.25em] text-accent-500 uppercase">
                    {sectionBadge}
                  </span>
                  <div className="w-6 h-px bg-accent-300 hidden md:block" />
                </div>
                <p className="text-lg md:text-2xl font-bold text-foreground-950 leading-snug">
                  {sectionNote}
                </p>
                <p className="text-base md:text-lg font-medium text-foreground-600 mt-1.5 leading-relaxed">
                  {sectionTags.join(' / ')}
                </p>
              </div>

              <div className="hidden md:flex flex-shrink-0 items-start">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-double-quotes-r text-accent-300 text-5xl opacity-40" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {products.map((product, i) => {
            const cardTags = getCardTags(product);
            const badgeVariant = product.isHot ? 'accent' : product.isNew ? 'secondary' : 'primary';
            const displayPrice = product.salePrice || product.price;
            const isSale = product.productType === 'Sale';

            return (
              <div
                key={product.id}
                ref={setRef(i)}
                className={`group bg-white rounded-2xl border border-background-200 overflow-hidden cursor-pointer hover:border-accent-300/50 hover:shadow-lg transition-all duration-500 scroll-reveal scroll-reveal-delay-${Math.min(i + 1, 6)} ${visible[i] ? 'scroll-reveal-visible' : ''}`}
                onClick={() => navigate(`/product/${product.slug || product.id}`)}
              >
                <div className="relative w-full h-56 md:h-72 overflow-hidden bg-gradient-to-br from-background-100 to-background-200">
                  <img
                    src={withImageFallback(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                  />

                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-primary-900/50 via-primary-900/15 to-transparent" />

                  {cardTags.length > 0 && (
                    <div className="absolute left-4 right-4 top-4 z-10 flex flex-wrap gap-1.5">
                      {cardTags.map((tag) => (
                        <span
                          key={tag}
                          className={`inline-block rounded-full px-3 py-1.5 text-xs font-bold tracking-wide shadow-sm backdrop-blur-sm ${
                            badgeVariant === 'accent'
                              ? 'bg-accent-500/90 text-background-50'
                              : badgeVariant === 'primary'
                                ? 'bg-primary-500/90 text-background-50'
                                : 'bg-secondary-500/90 text-background-50'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-eye-line text-primary-600 text-xl" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 md:p-6">
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">
                    {product.subtitle}
                  </span>

                  <h3 className="mt-1.5 text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-300">
                    {product.name}
                  </h3>

                  <div className="mt-4 mb-4 w-8 h-px bg-background-300 group-hover:w-16 group-hover:bg-accent-400 transition-all duration-500" />

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-foreground-500 font-medium">NT$</span>
                      <span className="text-2xl font-bold text-foreground-950">
                        {displayPrice.toLocaleString()}
                      </span>
                      {product.salePrice && <span className="ml-1 text-xs font-semibold text-foreground-500 line-through">NT$ {product.price.toLocaleString()}</span>}
                    </div>
                    <button
                      type="button"
                      title="查看商品"
                      aria-label="查看商品"
                      className="w-9 h-9 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center group-hover:bg-[#1B3D2A] transition-colors duration-300"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/product/${product.slug || product.id}`);
                      }}
                    >
                      <i className="ri-arrow-right-line text-sm text-primary-600 transition-colors duration-300 group-hover:text-background-50" />
                    </button>
                  </div>

                  {isSale ? (
                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] px-4 text-sm font-bold text-white transition-colors hover:bg-[#143026]"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleQuickAddToCart(product);
                        }}
                      >
                        <i className="ri-shopping-cart-2-line" />
                        {product.hasVariants ? '選擇規格' : '加入購物車'}
                      </button>
                      <button
                        type="button"
                        title="預約試戴"
                        aria-label="預約試戴"
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-background-200 text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-600"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(productTrialBookingPath(product));
                        }}
                      >
                        <i className="ri-calendar-check-line" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] px-4 text-sm font-bold text-background-50 transition-colors hover:bg-[#143026]"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(productTrialBookingPath(product));
                      }}
                    >
                      <i className="ri-calendar-check-line" />
                      預約試戴
                    </button>
                  )}
                  {cartFeedback?.id === product.id && (
                    <p className="mt-2 text-center text-xs font-medium text-[#c3423f]">{cartFeedback.message}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 md:hidden text-center">
          <button
            onClick={() => go(sectionButtonUrl)}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
          >
            {sectionButtonLabel}
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-background-50" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
